import type { ConfigData, UnitTemplate, UnitData, AffixDef } from '../../../types';
import { StateManager } from '../../../managers';
import { getPortraitUrl, getFullSpriteUrl } from '../../../combat/spriteConfig';
import { formatDefensiveTypesColored, formatRace, formatStatLine } from '../../components/GameTerms';
import { NYI_TEMPLATE_IDS, getConfig, getDiscoveredStrands } from './types';

/** Derive strand-eligibility tags from a unit template — mirrors server-side
 *  deriveStrandTags in unitService.ts. 'strand' is universal; mastery_<id> tags
 *  are added for each mastery the template has at tier ≤ 4 (Prodigy/Core/Affinity/Strong). */
function deriveStrandTags(t: UnitTemplate): string[] {
  const explicit = (t as { tags?: string[] }).tags;
  if (explicit && explicit.length > 0) return explicit;
  const masteryTags = Object.entries(t.masteryCosts ?? {})
    .filter(([, tier]) => (tier as number) <= 4)
    .map(([id]) => `mastery_${id}`);
  return ['strand', ...masteryTags];
}

/** Format the magnitude range an affix can roll across its tiers — used in the codex
 *  to summarize "this affix gives +X to +Y of stat". */
function formatAffixRange(def: AffixDef, configData: ConfigData | undefined): string {
  if (!def.tiers || def.tiers.length === 0) return '';
  const firstTier = def.tiers[0] as { minValue?: number; maxValue?: number; minLow?: number; maxHigh?: number };
  const lastTier = def.tiers[def.tiers.length - 1] as { minValue?: number; maxValue?: number; minLow?: number; maxHigh?: number };
  // Dual-range (flat damage): use minLow → maxHigh
  const lo = firstTier.minLow ?? firstTier.minValue ?? 0;
  const hi = lastTier.maxHigh ?? lastTier.maxValue ?? 0;
  if (def.category === 'mastery') {
    const name = configData?.masteries?.[def.masteryId!]?.name ?? def.masteryId!;
    return lo === hi ? `+${hi} ${name}` : `+${lo} to +${hi} ${name}`;
  }
  if (def.category === 'flat_damage') {
    const label = def.element ?? def.stat;
    return `+${lo} to +${hi} ${label} damage`;
  }
  // Otherwise reuse the unit-side stat line formatter
  const loLabel = formatStatLine(def.stat, def.modifier, lo);
  if (lo === hi) return loLabel;
  // Inject a "to +hi" between value and stat name
  return loLabel.replace(/^([+\-]?\d+%?)/, `$1 to ${def.modifier === 'percent' ? '+' + hi + '%' : '+' + hi}`);
}

export interface StrandsState {
  expandedStrandId: string | null;
  elementFilter: string;
}

export function createStrandsState(): StrandsState {
  return { expandedStrandId: null, elementFilter: '' };
}

export function renderStrands(content: HTMLElement, state: StrandsState): void {
  // If a strand is selected, show detail page instead of grid
  if (state.expandedStrandId) {
    renderStrandDetailPage(content, state);
    return;
  }

  const config = getConfig();
  if (!config) { content.innerHTML = '<p>Loading...</p>'; return; }

  const allTemplates = Object.values(config.unitTemplates).filter(t => !NYI_TEMPLATE_IDS.has(t.id));
  const discovered = getDiscoveredStrands();
  const discoveredCount = allTemplates.filter(t => discovered.has(t.id)).length;

  // Collect unique defensive types for filter pills
  const dtSet = new Set<string>();
  for (const t of allTemplates) {
    for (const dt of (t.defensiveTypes ?? [])) dtSet.add(dt);
  }
  const elements = [...dtSet].sort();

  // Apply defensive type filter
  const templates = state.elementFilter
    ? allTemplates.filter(t => (t.defensiveTypes ?? []).includes(state.elementFilter))
    : allTemplates;

  content.innerHTML = `
    <h2 class="codex-section-title">Strand Collection</h2>
    <p class="codex-section-subtitle">${discoveredCount} / ${allTemplates.length} discovered</p>
    <div class="codex-filter-pills">
      <button class="codex-pill${!state.elementFilter ? ' active' : ''}" data-el="">All</button>
      ${elements.map(el => `<button class="codex-pill element-${el}${state.elementFilter === el ? ' active' : ''}" data-el="${el}">${el}</button>`).join('')}
    </div>
    <div class="codex-card-grid codex-card-grid--tall">${templates.map(t => renderStrandCard(t, discovered.has(t.id))).join('')}</div>
  `;

  // Bind filter pills
  content.querySelectorAll('.codex-pill').forEach(pill => {
    pill.addEventListener('click', () => {
      state.elementFilter = (pill as HTMLElement).dataset.el || '';
      renderStrands(content, state);
    });
  });

  // Bind card clicks (only discovered)
  content.querySelectorAll('.codex-card[data-strand-id]:not(.undiscovered)').forEach(card => {
    card.addEventListener('click', () => {
      state.expandedStrandId = (card as HTMLElement).dataset.strandId!;
      renderStrands(content, state);
      content.scrollTop = 0;
    });
  });
}

function renderStrandCard(t: UnitTemplate, isDiscovered: boolean): string {
  const src = getPortraitUrl(t.spriteKey);
  if (!isDiscovered) {
    return `<div class="codex-card codex-card--tall undiscovered" data-strand-id="${t.id}">
      <div class="codex-card-sprite">${src ? `<img class="codex-sprite-img codex-sprite-silhouette" src="${src}" alt="Unknown" loading="lazy" draggable="false" />` : ''}</div>
      <div class="codex-card-name">???</div>
    </div>`;
  }
  const types = t.defensiveTypes ?? [];
  const dtLabel = types.length > 0 ? formatDefensiveTypesColored(types) : `<span class="${`element-${t.element}`}">${t.element}</span>`;
  return `<div class="codex-card codex-card--tall" data-strand-id="${t.id}">
    <div class="codex-card-sprite"><img class="codex-sprite-img" src="${src}" alt="${t.name}" loading="lazy" draggable="false" /></div>
    <div class="codex-card-name">${t.name}</div>
    <div class="codex-card-meta">${dtLabel}</div>
  </div>`;
}

function renderStrandDetailPage(content: HTMLElement, state: StrandsState): void {
  const config = getConfig();
  if (!config || !state.expandedStrandId) { state.expandedStrandId = null; renderStrands(content, state); return; }

  const t = config.unitTemplates[state.expandedStrandId];
  if (!t) { state.expandedStrandId = null; renderStrands(content, state); return; }

  const fullSrc = getFullSpriteUrl(t.spriteKey);
  const portraitSrc = getPortraitUrl(t.spriteKey);
  const spriteSrc = fullSrc || portraitSrc;

  const raceCap = formatRace(t.race);
  const dtLabel = formatDefensiveTypesColored(t.defensiveTypes ?? []);

  // Skins applicable to this strand
  const allSkins = config.skins
    ? Object.values(config.skins).filter(s => s.unitClasses.includes(t.id))
    : [];
  let skinDotsHtml = '';
  if (allSkins.length > 0) {
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const ownedSkins = unitData?.ownedSkins ?? {};
    const dots = [
      `<button class="strand-skin-dot active" data-skin-key="${t.spriteKey}" data-skin-name="" title="Default">
        <img src="${getPortraitUrl(t.spriteKey)}" alt="Default" draggable="false" />
      </button>`,
      ...allSkins.map(s => {
        const owned = (ownedSkins[s.id] ?? 0) > 0;
        return `<button class="strand-skin-dot${owned ? '' : ' locked'}" data-skin-key="${s.spriteKey}" data-skin-name="${s.name}" data-owned="${owned ? '1' : ''}" title="${s.name}${owned ? '' : ' (Not owned)'}">
          <img src="${getPortraitUrl(s.spriteKey)}" alt="${s.name}" draggable="false" />
          ${owned ? '' : '<span class="strand-skin-lock">🔒</span>'}
        </button>`;
      }),
    ].join('');
    skinDotsHtml = `
      <div class="strand-skin-browser">
        <div class="strand-skin-dots">${dots}</div>
        <div class="strand-skin-label"></div>
      </div>`;
  }

  // Mastery costs grouped by tier — compact table format. Tiers 1-8 per docs/MASTERY_REWORK_V2.md.
  let masteryCostHtml = '';
  if (t.masteryCosts) {
    const sorted = Object.entries(t.masteryCosts).sort((a, b) => a[1] - b[1]);
    const tierDefs = [
      { tier: 1, label: 'Prodigy',  color: '#ffb878' },
      { tier: 2, label: 'Core',     color: '#88e08c' },
      { tier: 3, label: 'Affinity', color: '#7ed8c8' },
      { tier: 4, label: 'Strong',   color: '#88b8e8' },
      { tier: 5, label: 'Moderate', color: '#cccc66' },
      { tier: 6, label: 'Distant',  color: '#ffaa44' },
      { tier: 7, label: 'Foreign',  color: '#cc88dd' },
      { tier: 8, label: 'Hostile',  color: '#ff5555' },
    ];
    const groups: Record<number, string[]> = {};
    for (const td of tierDefs) groups[td.tier] = [];
    for (const [id, costTier] of sorted) {
      const name = config.masteries[id]?.name ?? id;
      const t = (costTier as number) | 0;
      const bucket = groups[t] ?? groups[8];
      bucket.push(name);
    }
    masteryCostHtml = tierDefs.filter(td => groups[td.tier].length > 0).map(td =>
      `<div class="strand-mastery-row">
        <span class="strand-mastery-tier-label" style="color:${td.color}">${td.label}</span>
        <span class="strand-mastery-list">${groups[td.tier].join(', ')}</span>
      </div>`
    ).join('');
  }

  // Implicit pool — name + magnitude range + pick %
  let implicitHtml = '';
  if (t.implicitPool && t.implicitPool.length > 0) {
    const totalWeight = t.implicitPool.reduce((s, p) => s + p.weight, 0);
    implicitHtml = t.implicitPool.map(p => {
      const affix = config.affixes[p.affixId];
      const name = affix?.name ?? p.affixId;
      const desc = affix ? formatAffixRange(affix, config) : '';
      const pct = Math.round((p.weight / totalWeight) * 100);
      return `<div class="strand-implicit-row">
        <span class="strand-implicit-name">${name}</span>
        <span class="strand-implicit-desc">${desc}</span>
        <span class="strand-implicit-pct">${pct}%</span>
      </div>`;
    }).join('');
  }

  // Rollable explicit affix pool — derived from strand tags
  let rollableHtml = '';
  if (config.affixes) {
    const strandTags = new Set(deriveStrandTags(t));
    const eligible = Object.values(config.affixes).filter((a: AffixDef) =>
      a.type !== 'implicit' && a.tags.some(tag => strandTags.has(tag))
    );
    if (eligible.length > 0) {
      const prefixes = eligible.filter(a => a.type === 'prefix');
      const suffixes = eligible.filter(a => a.type === 'suffix');
      const rowFor = (a: AffixDef) => `<div class="strand-implicit-row">
        <span class="strand-implicit-name">${a.name}</span>
        <span class="strand-implicit-desc">${formatAffixRange(a, config)}</span>
      </div>`;
      rollableHtml = `
        ${prefixes.length > 0 ? `
          <div class="strand-affix-subhead">Prefixes (${prefixes.length})</div>
          ${prefixes.map(rowFor).join('')}
        ` : ''}
        ${suffixes.length > 0 ? `
          <div class="strand-affix-subhead">Suffixes (${suffixes.length})</div>
          ${suffixes.map(rowFor).join('')}
        ` : ''}
      `;
    }
  }

  content.innerHTML = `
    <button class="codex-back-btn" id="codex-strand-back">Back</button>
    <div class="codex-detail-page strand-detail">
      <div class="strand-hero">
        <div class="strand-hero-sprite">
          <img src="${spriteSrc}" alt="${t.name}" draggable="false" />
        </div>
        <div class="strand-hero-info">
          <h2 class="strand-hero-name">${t.name}</h2>
          <div class="strand-hero-meta">${raceCap} · ${dtLabel}${t.archetype ? ` · ${t.archetype}` : ''}</div>
          <div class="strand-hero-stats">
            <div class="strand-stat"><span class="strand-stat-val">${t.hp ?? '?'}</span><span class="strand-stat-label">Health</span></div>
            <div class="strand-stat"><span class="strand-stat-val">${t.spd ?? '?'}</span><span class="strand-stat-label">Speed</span></div>
            <div class="strand-stat"><span class="strand-stat-val">${(t as any).focus ?? '?'}</span><span class="strand-stat-label">Focus</span></div>
          </div>
        </div>
      </div>
      ${skinDotsHtml}
      ${t.pullQuote ? `<div class="strand-pull-quote">\u201C${t.pullQuote}\u201D</div>` : ''}
      ${t.loreParagraph ? `
        <div class="codex-detail-card">
          <div class="codex-detail-card-title">Lore</div>
          <p class="strand-lore-text">${t.loreParagraph}</p>
        </div>
      ` : ''}
      ${(t.leanInto?.length || t.playsWellWith?.length) ? `
        <div class="codex-detail-card">
          <div class="codex-detail-card-title">Identity</div>
          ${t.leanInto?.length ? `<div class="strand-identity-row"><span class="strand-identity-label">Lean Into</span><span class="strand-identity-pills">${t.leanInto.map(s => `<span class="strand-pill">${s}</span>`).join('')}</span></div>` : ''}
          ${t.playsWellWith?.length ? `<div class="strand-identity-row"><span class="strand-identity-label">Pairs With</span><span class="strand-identity-pills">${t.playsWellWith.map(s => `<span class="strand-pill">${s}</span>`).join('')}</span></div>` : ''}
        </div>
      ` : ''}
      ${masteryCostHtml ? `
        <div class="codex-detail-card">
          <div class="codex-detail-card-title">Mastery Affinity</div>
          ${masteryCostHtml}
        </div>
      ` : ''}
      ${implicitHtml ? `
        <div class="codex-detail-card">
          <div class="codex-detail-card-title">Implicit Pool</div>
          ${implicitHtml}
        </div>
      ` : ''}
      ${rollableHtml ? `
        <div class="codex-detail-card">
          <div class="codex-detail-card-title">Rollable Affixes</div>
          ${rollableHtml}
        </div>
      ` : ''}
    </div>
  `;

  content.querySelector('#codex-strand-back')?.addEventListener('click', () => {
    state.expandedStrandId = null;
    renderStrands(content, state);
    content.scrollTop = 0;
  });

  // Skin dot preview switching
  content.querySelectorAll('.strand-skin-dot').forEach(dot => {
    dot.addEventListener('click', () => {
      const skinKey = (dot as HTMLElement).dataset.skinKey!;
      const skinName = (dot as HTMLElement).dataset.skinName || '';
      const owned = !!(dot as HTMLElement).dataset.owned;

      // Swap hero sprite
      const heroImg = content.querySelector('.strand-hero-sprite img') as HTMLImageElement;
      if (heroImg) {
        heroImg.src = getFullSpriteUrl(skinKey) || getPortraitUrl(skinKey);
      }

      // Update active dot
      content.querySelectorAll('.strand-skin-dot').forEach(d => d.classList.remove('active'));
      dot.classList.add('active');

      // Update label
      const label = content.querySelector('.strand-skin-label') as HTMLElement;
      if (label) {
        if (!skinName) {
          label.textContent = '';
        } else {
          label.innerHTML = owned
            ? skinName
            : `${skinName} <span class="strand-skin-not-owned">· Not Owned</span>`;
        }
      }
    });
  });
}
